import { HubConnectionViewObject, UserRole, UserViewObject } from '@/api';
import { enumToArray } from '@/core/constants';
import { computed, ComputedRef } from 'vue';
import { Store } from '@/core/store/store';
import { mergeObject } from '@/core/util/mergeObjects';
import notificationsService from '@/core/notifications/notifications.service';
import bus from '@/core/bus';
import serverHubClient from '@/core/signalr/serverHub.client';
import { isProdMode } from '@/core/infrastructure/environment';

interface State extends Record<string, unknown> {
    identity: {
        user: UserViewObject | undefined | null;
    },
}

let websiteVersion = '';
const roleArray = enumToArray(UserRole);

class UserStore extends Store<State> {
    constructor() {
        super();
               
        bus.on('LOGGED_OUT', () => {
            this.clear();
        });

        serverHubClient.addEvent('ValidateVersion', (version: string) => {
            if (isProdMode()) {
                if (websiteVersion && websiteVersion !== version) {
                    notificationsService.notify({
                        title: 'Website opdatering',
                        message: 'Der er kommet en ny version af websitet. Siden bliver reloadet om 5 sekunder.',
                        duration: 0,
                    });
    
                    setTimeout(() => {
                        window.location.reload();
                    }, 5000);
                }

                websiteVersion = version;
            }
        });

        bus.on('UserUpdated', user => {
            if (this.state.identity.user?.id === user.id) {
                mergeObject(this.state.identity.user, user);
            }
        });

        bus.on('UsersUpdated', users => {
            for (let i = 0; i < users.length; i++) {
                const user = users[i];
                if (this.state.identity.user?.id === user.id) {
                    mergeObject(this.state.identity.user, user);
                    return;
                }
            }
        });

        bus.on('UserCreated', user => {
            if (this.state.identity.user?.id === user.id) {
                mergeObject(this.state.identity.user, user);
            }
        });

        serverHubClient.addEvent('OnConnected', (userId: string, connection: HubConnectionViewObject) => {
            if (this.state.identity.user && this.state.identity.user.id === userId) {
                (this.state.identity.user.activeConnections ??= []).push(connection);
            }
        });

        serverHubClient.addEvent('OnConnectionUpdate', (userId: string, connection: HubConnectionViewObject) => {
            if (this.state.identity.user && this.state.identity.user.id === userId) {
                if (!this.state.identity.user.activeConnections?.length) {
                    this.state.identity.user.activeConnections = [connection];
                } else {
                    const existingConnection = this.state.identity.user.activeConnections.find(x => x.connectionId === connection.connectionId);
                    if (existingConnection) {
                        mergeObject(existingConnection, connection);
                    } else {
                        this.state.identity.user.activeConnections.push(connection);
                    }
                }
            }
        });

        serverHubClient.addEvent('OnDisconnected', (userId: string, connectionId: string) => {
            if (this.state.identity.user?.activeConnections?.length && this.state.identity.user.id === userId) {
                const index = this.state.identity.user.activeConnections.findIndex(x => x.connectionId === connectionId);
                if (index >= 0) {
                    this.state.identity.user.activeConnections.splice(index, 1);
                }
            }
        });
    }
    
    protected data() {
        return {
            identity: {
                user: undefined,
            },
        };
    }

    public hasRole(role: UserRole) {
        return !!this.state.identity.user && roleArray.indexOf(this.state.identity.user.role) >= roleArray.indexOf(role);
    }

    public get isAdministrator(): ComputedRef<boolean> {
        return computed(() => this.hasRole(UserRole.Administrator));
    }

    public setUser(user: UserViewObject): void {
        this.state.identity.user = user;
    }

    public get isLoggedIn(): ComputedRef<boolean> {
        return computed(() => !!this.state.identity.user);
    }

    public get user(): ComputedRef<UserViewObject | undefined | null> {
        return computed(() => this.state.identity.user);
    }

    public get activeConnection(): ComputedRef<HubConnectionViewObject | null | undefined> {
        return computed(() => this.state.identity.user?.activeConnections?.find(x => x.connectionId === serverHubClient.connectionId.value));
    }

    public get isStaff(): ComputedRef<boolean> {
        return computed(() => this.state.identity.user?.isStaff ?? false);
    }

    public clear(): void {
        this.state.identity.user = undefined;
    }
}

export const userStore = new UserStore();
